import { useEffect, useMemo } from 'react'
import { queryKeys } from '@/hooks/query-keys'
import { useEntityRealtime } from '@/lib/realtime'
import { subscribeToQueryInvalidation } from '@/lib/realtime/shared-query-invalidation'
import { notificationReadsApi } from '@/modules/notifications/reads.api'
import { useQueryClient } from '@tanstack/react-query'

import type { NotificationFeedResponse } from '@scrambl/shared'

import { useNotificationReadSession } from './useNotificationReadSession'
import { useNotificationScopedQuery } from './useNotificationScopedQuery'

export const NOTIFICATION_FEED_QUERY_KEY = queryKeys.notifications.feed()

/**
 * Lane-based notification feed (action required / mentions / activity).
 * Stays live via the existing notification socket events — created/updated/
 * deleted all trigger one debounced refetch — with a slow polling fallback.
 */
export function useNotificationFeed(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options
  const session = useNotificationReadSession()
  const organizationId = session.enabled ? session.organizationId : null
  const queryKey = useMemo(
    () => [...NOTIFICATION_FEED_QUERY_KEY, session.key],
    [session],
  )
  const queryClient = useQueryClient()
  const { addEventListener, isConnected } = useEntityRealtime(
    'organization',
    organizationId ?? null,
    { suppressLogging: true },
  )

  const query = useNotificationScopedQuery(
    NOTIFICATION_FEED_QUERY_KEY,
    session,
    notificationReadsApi.feed,
    {
      enabled,
      staleTime: 20000,
      refetchInterval: isConnected ? 5 * 60_000 : 120_000,
    },
  )

  useEffect(() => {
    if (!enabled || !organizationId) return
    const invalidation = subscribeToQueryInvalidation(
      queryClient,
      organizationId,
      queryKey,
    )
    const scheduleInvalidate = () => {
      try {
        session.assertCurrent()
        invalidation.schedule()
      } catch {
        /* Retired listener. */
      }
    }

    const cleanups = [
      addEventListener('NOTIFICATION_CREATED', scheduleInvalidate),
      addEventListener('NOTIFICATION_UPDATED', scheduleInvalidate),
      addEventListener('NOTIFICATION_DELETED', scheduleInvalidate),
      addEventListener('UNREAD_COUNT_UPDATED', scheduleInvalidate),
    ]
    return () => {
      cleanups.forEach(cleanup => cleanup())
      invalidation.dispose()
    }
  }, [
    enabled,
    organizationId,
    session,
    queryKey,
    addEventListener,
    queryClient,
  ])

  return query
}

/**
 * THE "waiting on a decision from you" number. Every surface that claims
 * something needs the user — the bell badge, the Today briefing sentence —
 * must read this one figure so they can never disagree. It is the same
 * `actionRequired` lane the popover renders, so the badge and the list behind
 * it are always the same set.
 */
export const selectActionRequiredCount = (
  feed: NotificationFeedResponse | undefined,
): number => feed?.counts.actionRequired ?? 0

/** Hook form of {@link selectActionRequiredCount} for callers without the feed. */
export function useActionRequiredCount(options: { enabled?: boolean } = {}) {
  const { data } = useNotificationFeed(options)
  return selectActionRequiredCount(data)
}
